/* eslint react-hooks/rules-of-hooks: 0 */
import * as React from 'react'
import { startTransition } from 'react'
import { useRouter } from 'next/router'
import hoistNonReactStatics from 'hoist-non-react-statics'
import Cookies from 'js-cookie'
import useSWR from 'swr'

import type { UserType } from 'utils/api-mappers'
import { getV1UsersMeClient } from '@/generated/clients/axios/UsersService'
import { mapUserToUserType } from 'utils/api-type-mappers'
import * as store from 'utils/store'
import { useCountry } from './withCountry'

const AUTH_COOKIE_EXPIRY_DAYS = 365

export type AuthContextValue = {
  isRehydrating: boolean
  isRehydrated: boolean
  token: string | null
  userData: UserType | null
  setUserData: (userData: UserType) => void
  refreshUserData: () => Promise<void>
  login: (
    token: string,
    userData: UserType,
    rememberMe?: boolean,
  ) => Promise<void>
  logout: () => void
}

const defaultValue: AuthContextValue = {
  isRehydrating: false,
  isRehydrated: false,
  token: null,
  userData: null,
  setUserData: () => {},
  refreshUserData: async () => {},
  login: async () => {},
  logout: () => {},
}

const AuthContext: React.Context<AuthContextValue> =
  React.createContext(defaultValue)

// Global logout function that can be called from outside React context (e.g., interceptors)
let globalLogoutFunction: (() => void) | null = null

export function setGlobalLogoutFunction(logoutFn: (() => void) | null) {
  globalLogoutFunction = logoutFn
}

export function triggerGlobalLogout() {
  if (globalLogoutFunction) {
    try {
      globalLogoutFunction()
    } catch (error) {
      console.error('Error during global logout:', error)
      // Fallback: clear auth state manually if global logout fails
      if (typeof window !== 'undefined') {
        store.clear('authToken')
        store.clear('authUserData')
        Cookies.remove('authToken')
        window.location.href = '/'
      }
    }
  }
}

export default AuthContext

type Props = {
  authToken: string | null
}

export function withAuthContext<T extends Props>(
  WrappedComponent: React.ComponentType<T>,
) {
  function authContextHOC(props: T) {
    const router = useRouter()
    const { country } = useCountry()
    const [isRehydrating, setRehydratingState] = React.useState(true)
    const [isRehydrated, setRehydratedState] = React.useState(false)
    const [token, setToken] = React.useState(props.authToken)
    const [userData, setUserData] = React.useState<UserType | null>(
      null,
    )

    React.useEffect(() => {
      if (userData && typeof userData.id === 'number') {
        // TODO: Re-enable analytics when properly configured
        // const yandexAccountId = YANDEX_METRIKA_ID[country]
        // Analytics.setUserId(userData.id.toString(), yandexAccountId.toString())
        // TODO: Uncomment this when Sentry is configured
        // Sentry.configureScope((scope) => {
        //   scope.setUser({ id: userData.id.toString() })
        // })
      }
    }, [userData, country])

    React.useEffect(() => {
      async function rehydrate() {
        try {
          const nextUserData = await store.get('authUserData')
          startTransition(() => {
            setUserData(nextUserData)
          })
        } catch (error) {
          console.log(
            '[AuthContextHOC] Error while rehydrating user data',
            error,
          )
        } finally {
          startTransition(() => {
            setRehydratedState(true)
            setRehydratingState(false)
          })
        }
      }

      rehydrate()
    }, [])

    React.useEffect(() => {
      if (isRehydrated) {
        const { utm_source } = router.query

        if (typeof utm_source === 'string') {
          // TODO: Re-enable analytics when properly configured
          // manageSourceParameter(utm_source, token)
        }
      }
    }, [isRehydrated, router.query, token])

    const handleSetUserData = React.useCallback(function (
      nextUserData: UserType | null,
    ) {
      setUserData(nextUserData)
      store.set('authUserData', nextUserData)
    }, [])

    const refreshUserData = React.useCallback(
      async function refreshUserData() {
        // Skip silently if not logged in - callers can check authContext.token if needed
        if (!token || !userData) return

        const { data } = await getV1UsersMeClient()
        const nextUserData = mapUserToUserType(data)
        handleSetUserData(nextUserData)
      },
      [token, userData, handleSetUserData],
    )

    const handleLogin = React.useCallback(
      async function (
        nextToken: string,
        nextUserData: UserType,
        rememberMe?: boolean,
      ) {
        setToken(nextToken)
        setUserData(nextUserData)

        await store.set('authUserData', nextUserData)
        const isLocalhost = window.location.hostname === 'localhost'
        Cookies.set('authToken', nextToken, {
          expires: rememberMe ? AUTH_COOKIE_EXPIRY_DAYS : undefined,
          sameSite: 'Lax',
          secure: !isLocalhost,
        })
        const authRedirectedFrom = await store.get('authRedirectedFrom')

        if (authRedirectedFrom) {
          let from

          store.clear('authRedirectedFrom')

          try {
            from = new URL(authRedirectedFrom, self.location.href)
            router.replace({ pathname: from?.pathname })
          } catch (error) {
            console.error(error)
          }
        } else {
          router.replace(nextUserData.hasOrders ? '/parcels' : '/address')
        }
      },
      [router],
    )

    const handleLogout = React.useCallback(
      function () {
        setToken(null)
        setUserData(null)

        router.push('/')

        store.clear('authToken')
        store.clear('authUserData')
        Cookies.remove('authToken')
      },
      [router],
    )

    // Register the logout function globally so it can be called from interceptors
    React.useEffect(() => {
      setGlobalLogoutFunction(handleLogout)
      return () => {
        setGlobalLogoutFunction(null)
      }
    }, [handleLogout])

    const contextValue = React.useMemo(
      () => ({
        isRehydrating,
        isRehydrated,
        token,
        userData,
        setUserData: handleSetUserData,
        refreshUserData,
        login: handleLogin,
        logout: handleLogout,
      }),
      [
        isRehydrating,
        isRehydrated,
        token,
        userData,
        handleSetUserData,
        refreshUserData,
        handleLogin,
        handleLogout,
      ],
    )

    const { authToken: _authToken, ...restProps } = props
    return (
      <AuthContext.Provider value={contextValue as AuthContextValue}>
        <WrappedComponent {...({ ...restProps, authToken: token } as T)} />
      </AuthContext.Provider>
    )
  }

  hoistNonReactStatics(authContextHOC, WrappedComponent)
  return authContextHOC
}

export function useLatestUserData() {
  const authContext = React.useContext(AuthContext)
  const userId = authContext?.userData?.id
  const { data: userData } = useSWR(userId ? 'users/me' : null, async () => {
    const { data } = await getV1UsersMeClient()
    return mapUserToUserType(data)
  })

  React.useEffect(() => {
    if (userData) {
      authContext.setUserData(userData)
    }
  }, [userData, authContext])

  return authContext?.userData ?? userData
}
